import { yupResolver } from '@hookform/resolvers/yup';
import { isZorroOperationsRole } from '@zorro/common-utils';
import {
  useForm,
  useImpersonation,
  useMonolithMutation,
} from '@zorro/web/ui-utils';
import { UseFormReturn } from 'react-hook-form';

import { useAnalytics, usePageLoadAnalyticsEvent } from '../../../Analytics';
import {
  EnrollmentCallActorRole,
  LogEnrollmentCallFormFields,
  logEnrollmentCallSchemaObject,
} from './LogEnrollmentCallFormUtils';

export type UseLogEnrollmentCallForm = (props: {
  onboardingPeriodId: string;
  onClose: () => void;
}) => {
  logEnrollmentCallForm: UseFormReturn<LogEnrollmentCallFormFields>;
  onSubmit: () => Promise<void>;
  actorRole: EnrollmentCallActorRole;
};

export const useLogEnrollmentCallForm: UseLogEnrollmentCallForm = ({
  onboardingPeriodId,
  onClose,
}) => {
  const { authUser } = useImpersonation();
  const { sendEvent } = useAnalytics();

  const isZorroOperations = isZorroOperationsRole(
    authUser?.roles.map(({ id }) => id) ?? []
  );

  const actorRole: EnrollmentCallActorRole = isZorroOperations
    ? 'operator'
    : 'agent';

  const { tryMutate: logEnrollmentCall } = useMonolithMutation({
    method: 'onboardingPeriodsControllerLogEnrollmentCall',
    successMessage: 'Enrollment call logged',
  });

  const logEnrollmentCallForm = useForm<LogEnrollmentCallFormFields>({
    mode: 'all',
    resolver: yupResolver(logEnrollmentCallSchemaObject),
    defaultValues: { calledWith: '', notes: '' },
  });

  usePageLoadAnalyticsEvent({
    eventName: 'enrollment_call_log_opened',
    payload: {
      onboardingPeriodId,
      actorRole,
    },
  });

  const onSubmit = logEnrollmentCallForm.handleSubmit(async (data) => {
    const result = await logEnrollmentCall([
      onboardingPeriodId,
      {
        calledWith: data.calledWith,
        outcome: data.outcome,
        notes: data.notes || null,
      },
    ]);

    if (result.isOk()) {
      void sendEvent('enrollment_call_logged', {
        outcome: data.outcome,
        hasNotes: Boolean(data.notes),
        onboardingPeriodId,
        actorRole,
      });
      onClose();
    }
  });

  return {
    logEnrollmentCallForm,
    onSubmit,
    actorRole,
  };
};
